/**
 * Barra de progresso das navegações SPA, no estilo da VTEX: sai de 0, corre
 * até ~95% desacelerando enquanto o loader da rota roda, fecha em 100% quando
 * a página nova chega e some.
 *
 * Por que não o `NavigationProgress` do @decocms/tanstack (montado pelo
 * `DecoRootLayout`): ele é uma faixa em loop infinito, sem noção de avanço, e
 * não tem como configurar. Ele continua montado e invisível (o Tailwind v4 não
 * gera as classes dele, que moram em node_modules), então não há duas barras.
 */
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

/** Intervalo entre avanços enquanto o loader roda. */
const TRICKLE_MS = 200;
/** Teto enquanto não terminou — só a chegada da página leva a 100%. */
const MAX_WHILE_LOADING = 0.95;
/** Quanto tempo a barra cheia fica visível antes de sumir. */
const DONE_HOLD_MS = 250;
const FADE_MS = 300;
/** Vermelho do tema (`--color-error` em app.css). Literal porque o DaisyUI declara outro `--color-error`. */
const BAR_COLOR = "#EA1D25";

/** Avanço decrescente: rápido no começo, devagar perto do teto (como o NProgress). */
const nextProgress = (p: number) => {
    const inc = p < 0.2 ? 0.1 : p < 0.5 ? 0.04 : p < 0.8 ? 0.02 : 0.005;
    return Math.min(p + inc, MAX_WHILE_LOADING);
};

export default function NavigationProgressBar() {
    const isLoading = useRouterState({ select: (s) => s.isLoading });
    const [progress, setProgress] = useState(0);
    const [visible, setVisible] = useState(false);
    const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

    useEffect(() => {
        const clear = () => {
            timers.current.forEach(clearTimeout);
            timers.current = [];
        };
        clear();

        if (isLoading) {
            setVisible(true);
            setProgress(0.08);
            const trickle = setInterval(() => setProgress(nextProgress), TRICKLE_MS);
            return () => clearInterval(trickle);
        }

        // Terminou: fecha em 100%, segura um instante e some.
        setProgress((p) => (p > 0 ? 1 : 0));
        timers.current.push(
            setTimeout(() => setVisible(false), DONE_HOLD_MS),
            setTimeout(() => setProgress(0), DONE_HOLD_MS + FADE_MS),
        );
        return clear;
    }, [isLoading]);

    return (
        <div
            role='progressbar'
            aria-label='Carregando página'
            aria-hidden={!visible}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(progress * 100)}
            className='fixed top-0 left-0 right-0 pointer-events-none'
            style={{
                height: 3,
                zIndex: 9999,
                opacity: visible ? 1 : 0,
                transition: `opacity ${FADE_MS}ms ease`,
            }}
        >
            <div
                style={{
                    height: "100%",
                    backgroundColor: BAR_COLOR,
                    transform: `scaleX(${progress})`,
                    transformOrigin: "left",
                    // Ao zerar (barra já invisível) não anima a volta.
                    transition: progress === 0 ? "none" : `transform ${TRICKLE_MS}ms ease-out`,
                    boxShadow: `0 0 6px ${BAR_COLOR}`,
                }}
            />
        </div>
    );
}
